import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslations } from 'use-intl';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import { useTeams } from '@/app/queries';
import { Spinner } from '@/ui/Button';
import { EmptyState, PageHeader } from '@/ui/Panel';
import { Evaluations } from '@/routes/development/Evaluations';
import { Testing } from '@/routes/development/Testing';
import { Depth } from '@/routes/development/Depth';
import { Trials } from '@/routes/development/Trials';

const VIEWS = ['evaluations', 'testing', 'depth', 'trials'] as const;
type View = (typeof VIEWS)[number];

function isView(value: string | null): value is View {
  return value !== null && (VIEWS as readonly string[]).includes(value);
}

/**
 * Development, in three views over one subject.
 *
 * They share a page rather than splitting into three nav items because they
 * answer one question between them — is this player, and this squad, getting
 * better — and the view lives in the URL so a coach can send a colleague the
 * depth chart rather than directions to it.
 */
export function Development({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const teams = useTeams();
  const [params, setParams] = useSearchParams();
  const [teamId, setTeamId] = useState<string | null>(null);

  const view: View = isView(params.get('view')) ? (params.get('view') as View) : 'evaluations';
  const teamList = teams.data ?? [];
  const activeTeamId = teamId ?? teamList[0]?.id ?? null;

  const setView = (next: View) => {
    const updated = new URLSearchParams(params);
    updated.set('view', next);
    setParams(updated, { replace: true });
  };

  if (teams.isLoading) {
    return (
      <p className="flex items-center gap-2 text-ink-muted">
        <Spinner /> {t('common.loading')}
      </p>
    );
  }

  if (teamList.length === 0) {
    return (
      <>
        <PageHeader
          title={t('development.title')}
          lead={t('development.lead')}
          help={localePath(locale, '/guide/development')}
        />
        <EmptyState title={t('development.noTeam')} body={t('development.noTeamBody')} />
      </>
    );
  }

  return (
    <>
      <PageHeader
          title={t('development.title')}
          lead={t('development.lead')}
          help={localePath(locale, '/guide/development')}
        />

      {teamList.length > 1 ? (
        <div className="mb-4 flex flex-wrap gap-1.5">
          {teamList.map((tm) => (
            <button
              key={tm.id}
              type="button"
              onClick={() => setTeamId(tm.id)}
              aria-pressed={tm.id === activeTeamId}
              className={`rounded-(--radius-control) border px-3 py-1.5 text-meta transition-colors ${
                tm.id === activeTeamId
                  ? 'border-ink bg-ink text-paper'
                  : 'border-line bg-surface text-ink-muted hover:border-ink hover:text-ink'
              }`}
            >
              {tm.name}
            </button>
          ))}
        </div>
      ) : null}

      <div
        role="tablist"
        aria-label={t('development.title')}
        className="mb-5 flex gap-1 border-b border-line"
      >
        {VIEWS.map((v) => (
          <button
            key={v}
            role="tab"
            type="button"
            aria-selected={v === view}
            onClick={() => setView(v)}
            className={`-mb-px border-b-2 px-3 py-2 font-condensed text-subhead font-semibold tracking-tight transition-colors ${
              v === view
                ? 'border-ink text-ink'
                : 'border-transparent text-ink-faint hover:text-ink-muted'
            }`}
          >
            {t(
              v === 'evaluations'
                ? 'development.tabEvaluations'
                : v === 'testing'
                  ? 'development.tabTesting'
                  : v === 'depth'
                    ? 'development.tabDepth'
                    : 'development.tabTrials',
            )}
          </button>
        ))}
      </div>

      {activeTeamId === null ? null : view === 'evaluations' ? (
        <Evaluations locale={locale} teamId={activeTeamId} />
      ) : view === 'testing' ? (
        <Testing locale={locale} teamId={activeTeamId} />
      ) : view === 'depth' ? (
        <Depth locale={locale} teamId={activeTeamId} />
      ) : (
        <Trials locale={locale} teamId={activeTeamId} />
      )}
    </>
  );
}
